import moment from 'moment'

export const TICKET_STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' }
]

export const TICKET_PRIORITIES = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' }
]

export const TICKET_CATEGORIES = [
  { value: 'komplain_produk', label: 'Komplain Produk' },
  { value: 'pesanan', label: 'Pesanan' },
  { value: 'pembayaran', label: 'Pembayaran' },
  { value: 'pengiriman', label: 'Pengiriman' },
  { value: 'pertanyaan', label: 'Pertanyaan' },
  { value: 'lainnya', label: 'Lainnya' }
]

export const TICKET_FOLLOW_UP_FILTERS = [
  { value: '', label: 'Semua jadwal' },
  { value: 'has', label: 'Ada jadwal' },
  { value: 'overdue', label: 'Terlambat' },
  { value: 'none', label: 'Tanpa jadwal' }
]

function findLabel(items, value, fallback) {
  const found = items.find(item => item.value === value)
  return found ? found.label : fallback || value || '-'
}

export function ticketStatusLabel(value) {
  return findLabel(TICKET_STATUSES, value)
}

export function ticketPriorityLabel(value) {
  return findLabel(TICKET_PRIORITIES, value)
}

export function ticketCategoryLabel(value) {
  return findLabel(TICKET_CATEGORIES, value)
}

export function ticketStatusClass(value) {
  return 'is-' + (value || 'open').replace(/_/g, '-')
}

export function ticketPriorityClass(value) {
  return 'is-' + (value || 'normal')
}

export function formatTicketDate(unix, fallback) {
  if (!unix || Number(unix) <= 0) return fallback || '-'
  return moment.unix(Number(unix)).format('DD MMM YYYY')
}

export function formatTicketDateTime(unix, fallback) {
  if (!unix || Number(unix) <= 0) return fallback || '-'
  return moment.unix(Number(unix)).format('DD MMM YYYY HH:mm')
}

export function toUnixStart(dateString) {
  if (!dateString) return 0
  const value = moment(dateString, 'YYYY-MM-DD', true)
  return value.isValid() ? value.startOf('day').unix() : 0
}

export function toUnixEnd(dateString) {
  if (!dateString) return 0
  const value = moment(dateString, 'YYYY-MM-DD', true)
  return value.isValid() ? value.endOf('day').unix() : 0
}

export function toDatetimeLocal(unix) {
  if (!unix || Number(unix) <= 0) return ''
  return moment.unix(Number(unix)).format('YYYY-MM-DDTHH:mm')
}

export function datetimeLocalToUnix(value) {
  if (!value) return 0
  const parsed = moment(value, ['YYYY-MM-DDTHH:mm', 'YYYY-MM-DDTHH:mm:ss'], true)
  return parsed.isValid() ? parsed.unix() : 0
}

export function normalizeTicketPhone(value) {
  if (!value) return ''
  return String(value)
    .split('@')[0]
    .split(':')[0]
    .replace(/[^0-9]/g, '')
}

// ticketContactNumber memilih nomor dari master Contact dan selalu
// mengembalikan string. mobile_phone dipertahankan bila berupa nomor utuh
// (termasuk leading zero); phone_number dari API Contact bertipe number.
export function ticketContactNumber(contact) {
  if (!contact) return ''
  const mobile = String(contact.mobile_phone || '').trim()
  const phone = String(
    contact.phone_number || contact.contact_number || ''
  ).trim()
  const mobileDigits = normalizeTicketPhone(mobile)
  const phoneDigits = normalizeTicketPhone(phone)
  const mobileIsPhone = /^\+?[0-9]+$/.test(mobile) && mobileDigits.length >= 8
  const canonical = value => (value.indexOf('0') === 0 ? '62' + value.slice(1) : value)
  // mobile_phone dipertahankan bila mewakili nomor yang sama, agar leading
  // zero yang memang tersimpan tidak hilang. phone_number tetap menjadi
  // fallback utama untuk nomor kanonik.
  if (mobileIsPhone && phoneDigits && canonical(mobileDigits) === canonical(phoneDigits)) {
    return mobileDigits
  }
  if (phoneDigits) return phoneDigits
  if (mobileIsPhone) return mobileDigits
  return ''
}

export function formatTicketPhone(value, fallback) {
  const digits = normalizeTicketPhone(value)
  if (!digits) return typeof fallback === 'undefined' ? '-' : fallback
  if (digits.indexOf('62') === 0) return '+' + digits
  if (digits.indexOf('0') === 0) return '+62' + digits.slice(1)
  return '+' + digits
}

export function ticketActivityLabel(value) {
  const labels = {
    ticket_created: 'Tiket dibuat',
    status_changed: 'Status berubah',
    priority_changed: 'Prioritas berubah',
    agent_assigned: 'PIC berubah',
    description_updated: 'Informasi diperbarui',
    category_changed: 'Kategori berubah',
    internal_note: 'Catatan internal',
    follow_up_changed: 'Jadwal tindak lanjut',
    ticket_reopened: 'Tiket dibuka kembali',
    ticket_resolved: 'Tiket diselesaikan',
    contact_selected: 'Kontak pelanggan dipilih',
    license_changed: 'Nomor WhatsApp CS berubah',
    bulk_updated: 'Bulk update',
    auto_follow_up_scheduled: 'Auto Follow-up dijadwalkan',
    auto_follow_up_cancelled: 'Auto Follow-up dibatalkan',
    auto_follow_up_cancel_failed: 'Gagal membatalkan Auto Follow-up',
    auto_follow_up_completed: 'Auto Follow-up selesai',
    auto_follow_up_stopped: 'Auto Follow-up dihentikan',
    auto_follow_up_skipped: 'Auto Follow-up dilewati',
    auto_follow_up_failed: 'Auto Follow-up gagal'
  }
  return labels[value] || value || 'Aktivitas'
}

export function ticketActivityIcon(value) {
  const icons = {
    ticket_created: 'fa fa-ticket',
    status_changed: 'fa fa-exchange',
    priority_changed: 'fa fa-flag',
    agent_assigned: 'fa fa-user-plus',
    description_updated: 'fa fa-pencil',
    category_changed: 'fa fa-tags',
    internal_note: 'fa fa-sticky-note',
    follow_up_changed: 'fa fa-calendar',
    ticket_reopened: 'fa fa-undo',
    ticket_resolved: 'fa fa-check-circle',
    contact_selected: 'fa fa-user',
    license_changed: 'fa fa-whatsapp',
    bulk_updated: 'fa fa-check-square-o',
    auto_follow_up_scheduled: 'fa fa-calendar-plus-o',
    auto_follow_up_cancelled: 'fa fa-calendar-times-o',
    auto_follow_up_cancel_failed: 'fa fa-exclamation-triangle',
    auto_follow_up_completed: 'fa fa-check-circle-o',
    auto_follow_up_stopped: 'fa fa-stop-circle',
    auto_follow_up_skipped: 'fa fa-forward',
    auto_follow_up_failed: 'fa fa-exclamation-triangle'
  }
  return icons[value] || 'fa fa-circle'
}

export function extractApiErrorMessage(error, fallback) {
  if (!error) return fallback || 'Terjadi kesalahan'
  if (error.response && error.response.data && error.response.data.msg) {
    return error.response.data.msg
  }
  return error.message || error.msg || fallback || 'Terjadi kesalahan'
}



// WEBPACK FOOTER //
// ./src/utils/ticket.js